import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Breadcrumbs,
  Link,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Chip,
  Divider,
  Skeleton,
  useTheme,
  alpha,
  Tooltip,
  Avatar,
  Button
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import { useFavorites } from '../hooks/useFavorites';
import MDEditor from '@uiw/react-md-editor';
import { useThemeMode } from '../App';
import './markdown-styles.css';
import TableOfContents from '../components/TableOfContents';

const API_URL = 'https://guide.zerdegroup.kz/api/';

interface Article {
  id: number;
  title: string;
  content: string;
  category_id: number;
  subcategory_id: number;
  author_id: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  author: {
    id: number;
    login: string;
    role: string;
  };
  category: {
    id: number;
    title: string;
  };
  subcategory: {
    id: number;
    title: string;
  };
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('ru-RU', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  }).format(date);
};

const Article: React.FC = () => {
  const params = useParams();
  const id = params.id || '';
  const navigate = useNavigate();
  const { isFavorite, addToFavorites, removeFromFavorites } = useFavorites();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { mode } = useThemeMode();
  const theme = useTheme();

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetch(`${API_URL}/articles/${id}`);
        
        if (!response.ok) {
          throw new Error('Статья не найдена');
        }

        const data = await response.json();
        setArticle(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке статьи');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  const handleFavoriteClick = () => {
    if (isFavorite(article?.id.toString() || '')) {
      removeFromFavorites(article?.id.toString() || '');
    } else if (article) {
      addToFavorites({
        id: article.id.toString(),
        categoryId: article.category.id.toString(),
        subcategoryId: article.subcategory.id.toString(),
        title: article.title,
      });
    }
  };

  const processMarkdown = (content: string) => {
    return content.replace(/^(#{1,6})\s+(.+)$/gm, (match, hashes, title) => {
      const id = title
        .toLowerCase()
        .replace(/[^а-яёa-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      return `${hashes} ${title} <a id="${id}"></a>`;
    });
  };

  return (
    <Container maxWidth="lg" className="fade-in">
      {loading ? (
        <Box sx={{ py: 3 }}>
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="100%" height={60} sx={{ mb: 3 }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mb: 4 }} />
          <Skeleton variant="rectangular" width="100%" height={400} />
        </Box>
      ) : error || !article ? (
        <Alert 
          severity="error" 
          variant="filled"
          sx={{ 
            mt: 3, 
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          {error || 'Статья не найдена'}
        </Alert>
      ) : (
        <Box>
          <Box sx={{ mb: 6 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs 
                aria-label="breadcrumb"
                separator="/"
                sx={{
                  '& .MuiBreadcrumbs-separator': {
                    mx: 1,
                    color: alpha(theme.palette.text.secondary, 0.4),
                  },
                  '& a': {
                    color: alpha(theme.palette.text.secondary, 0.7),
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }
                }}
              >
                <Link onClick={() => navigate('/')}>
                  Главная
                </Link>
                <Link onClick={() => navigate(`/category/${article.category.id}`)}>
                  {article.category.title}
                </Link>
                <Link onClick={() => navigate(`/category/${article.category.id}/${article.subcategory.id}`)}>
                  {article.subcategory.title}
                </Link>
                <Typography 
                  color="text.primary"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {article.title}
                </Typography>
              </Breadcrumbs>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3 
            }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip 
                  size="small" 
                  label={article.category.title}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '24px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    }
                  }}
                  onClick={() => navigate(`/category/${article.category.id}`)}
                />
                <Chip 
                  size="small" 
                  label={article.subcategory.title}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                    color: 'secondary.main',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '24px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                    }
                  }}
                  onClick={() => navigate(`/category/${article.category.id}/${article.subcategory.id}`)}
                />
              </Box>
              <Button
                variant="text"
                size="small"
                startIcon={isFavorite(article.id.toString()) ? 
                  <FavoriteIcon sx={{ fontSize: '1.2rem', color: 'error.main' }} /> : 
                  <FavoriteBorderIcon sx={{ fontSize: '1.2rem' }} />
                }
                onClick={handleFavoriteClick}
                sx={{
                  color: isFavorite(article.id.toString()) ? 'error.main' : 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  minWidth: 'auto',
                  padding: '3px 8px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                    color: 'error.main',
                  },
                }}
              >
                {isFavorite(article.id.toString()) ? 'Удалить из избранного' : 'Добавить в избранное'}
              </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                  fontWeight: 800,
                  color: 'text.primary',
                  lineHeight: 1.2
                }}
              >
                {article.title}
              </Typography>
            </Box>

            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              color: 'text.secondary',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar 
                  sx={{ 
                    width: 28,
                    height: 28,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  {article.author.login[0].toUpperCase()}
                </Avatar>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.primary',
                    fontWeight: 500,
                    lineHeight: 1.2
                  }}
                >
                  {article.author.login}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: '1.1rem' }} />
                <Typography 
                  variant="body2"
                  sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}
                >
                  {formatDate(article.created_at)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <TableOfContents content={article.content} />
          </Box>

          <Box 
            data-color-mode={mode} 
            className="markdown-content"
            sx={{ 
              '& pre': {
                borderRadius: 2,
                p: 3,
                backgroundColor: mode === 'dark' ? alpha('#000', 0.3) : alpha('#f6f8fa', 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
              },
              '& code': {
                fontFamily: 'monospace',
                backgroundColor: mode === 'dark' ? alpha('#000', 0.3) : alpha('#f6f8fa', 0.8),
                p: 0.5,
                borderRadius: 1,
              },
              '& img': {
                maxWidth: '100%',
                borderRadius: 2,
                boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
              },
              '& blockquote': {
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                pl: 2,
                ml: 0,
                backgroundColor: mode === 'dark' ? alpha('#000', 0.2) : alpha('#f6f8fa', 0.5),
                py: 1,
                borderRadius: '0 4px 4px 0',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                transition: 'all 0.2s',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.dark',
                }
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 4,
                mb: 2,
                fontWeight: 700,
                position: 'relative',
                pl: 3,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 4,
                  height: '70%',
                  backgroundColor: 'primary.main',
                  borderRadius: 4,
                }
              },
              '& h1': { fontSize: '2.5rem' },
              '& h2': { fontSize: '2rem' },
              '& h3': { fontSize: '1.75rem' },
              '& h4': { fontSize: '1.5rem' },
              '& ul, & ol': {
                pl: 3,
                mb: 3,
                '& li': {
                  mb: 1,
                }
              }
            }}
          >
            <MDEditor.Markdown 
              source={processMarkdown(article.content)}
              style={{ 
                backgroundColor: 'transparent',
                color: theme.palette.text.primary
              }}
            />
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default Article; 